import * as React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { BorrowerChild } from "../LinkedAccountsPage";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import { AbstractBaseResponse } from "@tlc/ls2pac/components/ForgotPINDialog";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import axios from "axios";

interface SetGuardianProhibitedListChildrenResponse
  extends AbstractBaseResponse {}
async function setProhibitedListChildren(listId: number, childIds: number[]) {
  const { data } = await axios.post<SetGuardianProhibitedListChildrenResponse>(
    `/list/prohibited-list/${listId}/children`,
    childIds
  );
  return data.status;
}

export interface ChildSelectionDialogProps {
  open: boolean; // externally controlled open state
  onClose: () => void;
  onSave: (update: BorrowerChild["id"][]) => void;
  children: BorrowerChild[];
  listId: number;
  childIds: BorrowerChild["id"][];
}

interface ChildCheckbox {
  id: number;
  childId: number;
  checked: boolean;
  issuingLocation: number;
  label: string;
}

interface ChildSelectionFormInput {
  children: ChildCheckbox[];
}

export const ChildSelectionDialog: React.FC<ChildSelectionDialogProps> = ({
  open,
  onClose,
  onSave,
  children,
  childIds,
  listId,
}) => {
  const { handleSubmit, control, reset } = useForm<ChildSelectionFormInput>({
    mode: "onBlur",
    defaultValues: {
      children: children.map((child) => ({
        childId: child.id,
        label: `${child.firstName} ${child.lastName}`,
        issuingLocation: child.issuingLocation,
        checked: childIds.includes(child.id),
      })),
    },
  });
  const { fields } = useFieldArray({
    control,
    name: "children", // Name of the field array
  });
  const { translate } = useLanguage();
  const { locationConfiguration } = useLocationConfiguration();
  const configLocationIds = (locationConfiguration?.filterBranches || []).map(
    (branch) => parseInt(branch.identifier)
  );

  const schoolStudents = children.filter(
    (field) => configLocationIds.indexOf(field.issuingLocation) !== -1
  );
  const notSchoolStudents = children.filter(
    (field) => configLocationIds.indexOf(field.issuingLocation) === -1
  );

  const onSubmit = async (formData: ChildSelectionFormInput) => {
    const checkedChildIds = formData.children
      .filter((child) => child.checked)
      .map((child) => child.childId);
    const { success, message } = await setProhibitedListChildren(
      listId,
      checkedChildIds
    );
    if (success) {
      onSave(checkedChildIds);
    } else {
      // TODO we have no pre-existing error handling here
      console.error(message);
    }
  };

  const getChildCheckbox = (child: ChildCheckbox, index: number) => {
    return (
      <Controller
        key={child.id}
        control={control}
        name={`children.${index}.checked`}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            control={<Checkbox checked={value} onChange={onChange} />}
            label={child.label}
          />
        )}
      />
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>{translate("associatedStudents")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {translate("associatedStudentsDirections")}
        </DialogContentText>
        {configLocationIds.length === 0 && (
          <>
            <FormGroup>
              {fields.map((child, index) => {
                return getChildCheckbox(child, index);
              })}
            </FormGroup>
          </>
        )}
        {configLocationIds.length > 0 && (
          <>
            {schoolStudents.length > 0 && (
              <>
                <DialogContentText>
                  {locationConfiguration.libraryName
                    ? translate("studentsAtLibraryName", {
                        LIBRARY_NAME: locationConfiguration.libraryName,
                      })
                    : translate("studentsAtThisSchool")}
                </DialogContentText>
                <FormGroup>
                  {fields.map((child, index) => {
                    if (configLocationIds.includes(child.issuingLocation)) {
                      return getChildCheckbox(child, index);
                    }
                    return "";
                  })}
                </FormGroup>
              </>
            )}
            {notSchoolStudents.length > 0 && (
              <>
                <DialogContentText>
                  {locationConfiguration.libraryName
                    ? translate("studentsNotAtLibraryName", {
                        LIBRARY_NAME: locationConfiguration.libraryName,
                      })
                    : translate("studentsNotAtThisSchool")}
                </DialogContentText>
                <Alert severity="info">{translate("studentsNotAtHint")}</Alert>
                <FormGroup>
                  {fields.map((child, index) => {
                    if (!configLocationIds.includes(child.issuingLocation)) {
                      return getChildCheckbox(child, index);
                    }
                    return "";
                  })}
                </FormGroup>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{translate("cancel")}</Button>
        <Button variant="contained" type="submit">
          {translate("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
